import { useState, useCallback } from 'react';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
}

interface DialogState extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

let _open: ((opts: ConfirmOptions) => Promise<boolean>) | null = null;

/**
 * Call this anywhere to show a styled confirmation dialog.
 * Returns true if the user clicked Confirm, false if they clicked Cancel.
 *
 * Usage:
 *   const ok = await confirm({ message: 'Delete this invoice?' });
 *   if (ok) del.mutate(id);
 */
export async function confirm(opts: ConfirmOptions): Promise<boolean> {
  if (!_open) {
    // Provider not mounted yet — fail safe (deny) rather than using window.confirm
    // which is suppressed in Electron's sandbox and always returns false anyway.
    return false;
  }
  return _open(opts);
}

export function ConfirmDialogProvider() {
  const [state, setState] = useState<DialogState | null>(null);

  _open = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setState({ ...opts, resolve });
    });
  }, []);

  if (!state) return null;

  function finish(ok: boolean) {
    state!.resolve(ok);
    setState(null);
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={() => finish(false)}
    >
      <div
        style={{
          background: 'var(--surface, #fff)',
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
          padding: '1.5rem',
          maxWidth: 420,
          width: '100%',
        }}
        onClick={e => e.stopPropagation()}
      >
        {state.title && (
          <h2 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 700, color: 'var(--text)' }}>
            {state.title}
          </h2>
        )}
        <p style={{ margin: '0 0 1.25rem', fontSize: '0.9rem', color: 'var(--text-2, #475569)', lineHeight: 1.55 }}>
          {state.message}
        </p>
        <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
          <button
            className="btn btn-ghost"
            onClick={() => finish(false)}
            autoFocus
          >
            Cancel
          </button>
          <button
            className={`btn ${state.danger ? 'btn-danger' : 'btn-primary'}`}
            onClick={() => finish(true)}
          >
            {state.confirmLabel ?? (state.danger ? 'Delete' : 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
